import React from 'react';
import { CheckCircle } from 'lucide-react';

/**
 * Reusable Horizontal Stepper for multi-step forms.
 * 
 * @param {Array} steps - Array of step objects { id, label, icon }
 * @param {number} currentStep - Index of the current active step
 * @param {function} onStepClick - Callback when a completed step is clicked
 */
const FormStepper = ({ steps, currentStep, onStepClick }) => {
    return (
        <div style={{
            background: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '0',
            padding: '24px 32px',
            marginBottom: '0',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                position: 'relative',
            }}>
                {/* Connector line behind steps */}
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: `calc(100% / ${steps.length * 2})`,
                    right: `calc(100% / ${steps.length * 2})`,
                    height: '2px',
                    background: 'var(--border-color)',
                    zIndex: 0,
                }} />

                {/* Progress fill */}
                <div style={{
                    position: 'absolute',
                    top: '19.5px',
                    left: `calc(100% / ${steps.length * 2})`,
                    width: `${(currentStep / (steps.length - 1)) * (100 - (100 / steps.length))}%`,
                    height: '3px',
                    background: 'var(--gold-bright)',
                    zIndex: 1,
                    transition: 'width 0.4s ease',
                }} />

                {steps.map((step, idx) => {
                    const isCompleted = idx < currentStep;
                    const isActive = idx === currentStep;
                    const StepIcon = step.icon;

                    return (
                        <div
                            key={step.id || idx}
                            onClick={() => isCompleted && onStepClick && onStepClick(idx)}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: isCompleted ? 'pointer' : 'default',
                                flex: 1,
                                zIndex: 2,
                                position: 'relative',
                            }}
                        >
                            {/* Circle */}
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: isCompleted
                                    ? 'var(--gold-bright)'
                                    : isActive ? 'var(--gold-primary)' : 'white',
                                border: `2px solid ${isCompleted ? 'var(--gold-bright)' : isActive ? 'var(--gold-primary)' : 'var(--divider-color)'}`,
                                transition: 'all 0.3s ease',
                                boxShadow: isCompleted
                                    ? '0 0 0 4px var(--gold-glow)'
                                    : isActive ? '0 0 0 4px var(--gold-glow-active)' : 'none',
                                color: isCompleted || isActive ? 'white' : 'var(--text-muted)',
                            }}>
                                {isCompleted
                                    ? <CheckCircle size={18} />
                                    : <StepIcon size={16} />
                                }
                            </div>

                            {/* Label */}
                            <div style={{
                                marginTop: '8px',
                                fontSize: '11px',
                                fontWeight: isActive ? '700' : isCompleted ? '600' : '500',
                                color: isCompleted ? 'var(--gold-text-completed)' : isActive ? 'var(--gold-primary)' : 'var(--text-muted)',
                                textAlign: 'center',
                                fontFamily: 'var(--font-family)',
                                maxWidth: '80px',
                                lineHeight: '1.3',
                                transition: 'color 0.3s ease',
                            }}>
                                {step.label}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FormStepper;
